import { useState } from 'react';
import { Loader2, Lock, Mail, User2, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { BrandLogo } from '../layout/BrandLogo';
import { toast } from 'sonner';

function validatePassword(password) {
  if (password.length < 8) return 'Use at least 8 characters.';
  if (!/[A-Z]/.test(password)) return 'Include at least one uppercase letter.';
  if (!/[a-z]/.test(password)) return 'Include at least one lowercase letter.';
  if (!/\d/.test(password)) return 'Include at least one number.';
  return '';
}

export function AuthModal({ isOpen, onClose }) {
  const {
    signInWithPassword,
    registerWithPassword,
    signInWithEmail,
    signInWithGoogle,
    authError,
    clearError,
    hasRemoteAuth,
  } = useAuth();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [mode, setMode] = useState('signin');

  if (!isOpen) return null;

  const passwordHint = mode === 'signup' ? validatePassword(password) : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();

    if (!email.trim() || !password.trim() || (mode === 'signup' && !fullName.trim())) return;
    if (mode === 'signup' && passwordHint) return;
    if (mode === 'signup' && password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    const { error } =
      mode === 'signup'
        ? await registerWithPassword(fullName.trim(), email.trim(), password)
        : await signInWithPassword(email.trim(), password);
    setIsLoading(false);

    if (!error) {
      toast.success(mode === 'signup' ? 'Account created successfully.' : 'Signed in successfully.');
      handleClose();
    }
  };

  const handleMagicLink = async () => {
    setIsLoading(true);
    clearError();
    const { error } = await signInWithEmail(email.trim());
    setIsLoading(false);
    if (!error) {
      setEmailSent(true);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    clearError();
    await signInWithGoogle();
  };

  const handleClose = () => {
    setEmail('');
    setFullName('');
    setPassword('');
    setConfirmPassword('');
    setEmailSent(false);
    setMode('signin');
    clearError();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-[rgba(31,24,20,0.48)] backdrop-blur-md"
      onClick={handleClose}
      data-testid="auth-modal-overlay"
    >
      <div className="flex min-h-full items-start justify-center overflow-y-auto p-4 md:items-center md:p-6">
        <div
          className="relative my-6 w-full max-w-[1040px] overflow-hidden rounded-[36px] border border-[#E7DED3] bg-[#FFF9F4] shadow-[0_35px_120px_rgba(48,35,24,0.24)] md:my-0 md:max-h-[calc(100vh-3rem)]"
          onClick={(e) => e.stopPropagation()}
          data-testid="auth-modal"
        >
          <button
            onClick={handleClose}
            className="absolute right-5 top-5 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-[#E7DED3] bg-white/90 text-[#7E766F] transition hover:text-[#221B17]"
            aria-label="Close"
            data-testid="auth-modal-close"
          >
            <X size={18} />
          </button>

          <div className="grid min-h-[640px] md:grid-cols-[1.05fr_0.95fr]">
            <div className="relative hidden overflow-hidden border-r border-[#ECE2D7] bg-[radial-gradient(circle_at_top_left,rgba(244,227,197,0.55),transparent_34%),linear-gradient(160deg,#F8F0E8_0%,#FBF7F2_45%,#F5EBE2_100%)] p-10 md:flex md:flex-col">
              <div className="absolute inset-0 opacity-60">
                <div className="absolute -left-20 top-24 h-72 w-72 rounded-full bg-[rgba(255,255,255,0.52)] blur-3xl" />
                <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-[rgba(212,175,55,0.08)] blur-3xl" />
              </div>

              <div className="relative z-10 max-w-md">
                <BrandLogo
                  iconSize={36}
                  titleClassName="text-2xl text-[#221B17]"
                  subtitleClassName="text-xs tracking-[0.24em] text-[#8B8379]"
                />

                <h2 className="mt-16 max-w-sm font-serif text-5xl leading-[1.02] tracking-[-0.04em] text-[#221B17]">
                  Build a secure invitation studio account.
                </h2>
                <p className="mt-5 max-w-md text-base leading-7 text-[#6F675F]">
                  Save drafts, manage exports, and protect every invitation behind a proper account instead of an open browser session.
                </p>
              </div>

              <div className="relative z-10 mt-auto rounded-[32px] border border-[rgba(231,222,211,0.9)] bg-white/80 p-6 shadow-[0_24px_70px_rgba(77,59,41,0.1)] backdrop-blur-md">
                <div className="text-xs uppercase tracking-[0.2em] text-[#9B9288]">What unlocks</div>
                <div className="mt-5 space-y-4">
                  {[
                    'A protected library for saved invitations and revisions.',
                    'Download access for every invitation format.',
                    'A cleaner path into subscriptions and future payment flows.',
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-3 text-sm leading-6 text-[#4D453E]">
                      <div className="mt-1.5 h-2.5 w-2.5 rounded-full bg-[#D2B15A]" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex max-h-[calc(100vh-6rem)] flex-col justify-center overflow-y-auto px-5 py-8 md:max-h-[calc(100vh-3rem)] md:px-12">
              {emailSent ? (
                <div className="mx-auto max-w-md text-center">
                  <div className="font-serif text-3xl text-[#221B17]">Check your inbox</div>
                  <p className="mt-4 text-sm leading-7 text-[#6F675F]">
                    We sent a secure sign-in link to <span className="font-medium text-[#221B17]">{email}</span>.
                  </p>
                  <button
                    onClick={() => setEmailSent(false)}
                    className="mt-8 rounded-full border border-[#E7DED3] bg-white px-6 py-3 text-sm font-medium text-[#221B17]"
                  >
                    Use another email
                  </button>
                </div>
              ) : (
                <div className="mx-auto w-full max-w-md">
                  <div className="mb-8 md:hidden">
                    <BrandLogo
                      iconSize={36}
                      titleClassName="text-2xl text-[#221B17]"
                      subtitleClassName="text-xs text-[#8B8379]"
                    />
                  </div>

                  <div className="text-center md:text-left">
                    <div className="text-xs uppercase tracking-[0.24em] text-[#A2968A]">Studio access</div>
                    <h2 className="mt-3 font-serif text-4xl leading-none tracking-[-0.04em] text-[#221B17]">
                      {mode === 'signup' ? 'Create your account' : 'Sign in securely'}
                    </h2>
                    <p className="mt-4 text-sm leading-7 text-[#7E766F]">
                      Register with a secure password, then sign in to save work and download invitations.
                    </p>
                  </div>

                  {authError && (
                    <div className="mt-6 rounded-[22px] border border-[#F2C7C2] bg-[#FFF4F2] px-4 py-4 text-sm text-[#A94D45]">
                      {authError}
                    </div>
                  )}

                  <div className="mt-8 rounded-[28px] border border-[#ECE2D7] bg-white p-4 shadow-[0_20px_60px_rgba(77,59,41,0.07)]">
                    <div className="mb-6 grid grid-cols-2 gap-2 rounded-[22px] bg-[#F7F1EA] p-1">
                      <button
                        type="button"
                        onClick={() => setMode('signin')}
                        className={`rounded-[18px] px-4 py-3 text-sm font-medium transition ${
                          mode === 'signin' ? 'bg-white text-[#221B17] shadow-[0_8px_22px_rgba(67,47,29,0.08)]' : 'text-[#7E766F]'
                        }`}
                      >
                        Sign in
                      </button>
                      <button
                        type="button"
                        onClick={() => setMode('signup')}
                        className={`rounded-[18px] px-4 py-3 text-sm font-medium transition ${
                          mode === 'signup' ? 'bg-white text-[#221B17] shadow-[0_8px_22px_rgba(67,47,29,0.08)]' : 'text-[#7E766F]'
                        }`}
                      >
                        Register
                      </button>
                    </div>

                    {hasRemoteAuth ? (
                      <>
                        <button
                          onClick={handleGoogleSignIn}
                          disabled={isLoading}
                          className="flex w-full items-center justify-center gap-3 rounded-[22px] border border-[#E9E0D5] bg-[#FCFAF8] px-4 py-4 text-sm font-medium text-[#221B17] transition hover:bg-white disabled:opacity-50"
                          data-testid="google-signin-button"
                        >
                          Continue with Google
                        </button>
                        <div className="relative my-6">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-[#EFE6DB]" />
                          </div>
                          <div className="relative flex justify-center">
                            <span className="bg-white px-4 text-[0.7rem] uppercase tracking-[0.24em] text-[#A79C90]">
                              Or password
                            </span>
                          </div>
                        </div>
                      </>
                    ) : null}

                    <form onSubmit={handleSubmit}>
                      {mode === 'signup' ? (
                        <div className="mb-4">
                          <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-[#8E857A]">
                            Full name
                          </label>
                          <div className="relative">
                            <User2 size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A79C90]" />
                            <input
                              type="text"
                              value={fullName}
                              onChange={(e) => setFullName(e.target.value)}
                              placeholder="Your full name"
                              className="w-full rounded-[22px] border border-[#E9E0D5] bg-[#FCFAF8] py-4 pl-12 pr-4 text-sm text-[#221B17] outline-none transition focus:border-[#D5C0A3] focus:bg-white"
                              disabled={isLoading}
                              required
                            />
                          </div>
                        </div>
                      ) : null}

                      <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-[#8E857A]">
                        Email address
                      </label>
                      <div className="relative">
                        <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A79C90]" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          className="w-full rounded-[22px] border border-[#E9E0D5] bg-[#FCFAF8] py-4 pl-12 pr-4 text-sm text-[#221B17] outline-none transition focus:border-[#D5C0A3] focus:bg-white"
                          disabled={isLoading}
                          required
                        />
                      </div>

                      <label className="mb-2 mt-4 block text-xs uppercase tracking-[0.18em] text-[#8E857A]">
                        Password
                      </label>
                      <div className="relative">
                        <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A79C90]" />
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter a secure password"
                          className="w-full rounded-[22px] border border-[#E9E0D5] bg-[#FCFAF8] py-4 pl-12 pr-4 text-sm text-[#221B17] outline-none transition focus:border-[#D5C0A3] focus:bg-white"
                          disabled={isLoading}
                          required
                        />
                      </div>

                      {mode === 'signup' ? (
                        <>
                          <p className={`mt-2 text-xs ${passwordHint ? 'text-[#A94D45]' : 'text-[#7E766F]'}`}>
                            {passwordHint || 'Use 8+ characters with uppercase, lowercase, and a number.'}
                          </p>
                          <label className="mb-2 mt-4 block text-xs uppercase tracking-[0.18em] text-[#8E857A]">
                            Confirm password
                          </label>
                          <div className="relative">
                            <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A79C90]" />
                            <input
                              type="password"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              placeholder="Re-enter your password"
                              className="w-full rounded-[22px] border border-[#E9E0D5] bg-[#FCFAF8] py-4 pl-12 pr-4 text-sm text-[#221B17] outline-none transition focus:border-[#D5C0A3] focus:bg-white"
                              disabled={isLoading}
                              required
                            />
                          </div>
                        </>
                      ) : null}

                      <button
                        type="submit"
                        disabled={isLoading || !email.trim() || !password.trim() || (mode === 'signup' && (!fullName.trim() || Boolean(passwordHint) || password !== confirmPassword))}
                        className="mt-4 flex w-full items-center justify-center gap-2 rounded-[22px] bg-[#1F1A17] px-5 py-4 text-sm font-medium text-white transition hover:bg-[#2B2420] disabled:opacity-50"
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="animate-spin" size={18} />
                            {mode === 'signup' ? 'Creating account...' : 'Signing in...'}
                          </>
                        ) : mode === 'signup' ? (
                          'Create account'
                        ) : (
                          'Sign in'
                        )}
                      </button>
                    </form>

                    {hasRemoteAuth ? (
                      <button
                        type="button"
                        onClick={handleMagicLink}
                        disabled={isLoading || !email.trim()}
                        className="mt-4 w-full rounded-[22px] border border-[#E9E0D5] bg-[#FCFAF8] px-4 py-4 text-sm font-medium text-[#221B17] transition hover:bg-white disabled:opacity-50"
                      >
                        Send magic link instead
                      </button>
                    ) : null}
                  </div>

                  <p className="mt-6 text-center text-xs leading-6 text-[#958A80] md:text-left">
                    By continuing, you agree to our Terms of Service and Privacy Policy.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
